<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { formsApi, type Form } from '@/services/api/forms';
import { sectionsApi, type FormSection } from '@/services/api/sections';
import { useNotifier } from '@/composables/notifier';

const route = useRoute();
const notify = useNotifier();

const form = ref<Form | null>(null);
const sections = ref<FormSection[]>([]);
const submitting = ref(false);

function ids(): { projectId: string; formId: string } {
  const p = route.params.id;
  const f = route.params.form_id ?? route.params.form;
  return {
    projectId: Array.isArray(p) ? (p[0] ?? '') : p ?? '',
    formId: Array.isArray(f) ? (f[0] ?? '') : f ?? '',
  };
}

async function reload(): Promise<void> {
  const { projectId, formId } = ids();
  form.value = await formsApi.get(projectId, formId);
  sections.value = await sectionsApi.index(projectId, formId);
}

async function save(): Promise<void> {
  if (!form.value || submitting.value) return;
  submitting.value = true;
  try {
    const { projectId } = ids();
    form.value = await formsApi.update(projectId, form.value as Form & { id: number });
    notify.info('Form saved');
  } finally {
    submitting.value = false;
  }
}

onMounted(reload);
</script>

<template>
  <section v-if="form" class="resource">
    <h1>Edit Form</h1>
    <form class="resource__form" @submit.prevent="save">
      <label><span>Name</span><input v-model="form.name" type="text" required /></label>
      <label><span>Description</span><textarea v-model="form.description" rows="4" /></label>
      <label><span>SKU</span><input v-model="form.sku" type="text" /></label>
      <button type="submit" :disabled="submitting">{{ submitting ? 'Saving…' : 'Save Form' }}</button>
    </form>

    <h2 class="resource__subtitle">Sections</h2>
    <ul class="resource__list">
      <li v-for="s in sections" :key="s.id">
        <span>{{ s.name }} — {{ s.display_name }}</span>
      </li>
    </ul>
    <p v-if="sections.length === 0">No sections yet.</p>
  </section>
  <p v-else class="resource">Loading…</p>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
<style scoped>
.resource__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 32rem;
}
.resource__form input,
.resource__form textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.resource__form button {
  align-self: flex-start;
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}
.resource__subtitle {
  margin-top: 1.5rem;
}
</style>
